import { Button, Divider } from "@mui/material";
import React, { useEffect, useState } from "react";
import { NetworkRequest } from "../../../common/models";
import { ApiRouteHelpers, formatUrl } from "../../../common/root/apiRoutes";
import { DataRequestEventCard } from "../../components/dataRequest/DataRequestEventCard";
import { Empty } from "../../components/Empty";
import { PageHeader } from "../../components/layout/header/Header";
import { PageHeaderBack } from "../../components/layout/header/PageHeaderBack";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { useQuery } from "../../hooks/useQuery";
import { useRequestsForConsumer } from "../../hooks/useRequests";
import { Routes } from "../../root/routes";

type Props = {};

export type ConsumerDetailPageParams = {
  subjectEmailAddress?: string;
  subjectNameFirst?: string;
  subjectNameLast?: string;
  subjectNameLimit?: number; // Set to 1 if only checking for at least one match.
  requestPublicId?: string;
};

export const ConsumerDetailPage: React.FC<Props> = () => {
  const consumerDetails = useQuery<ConsumerDetailPageParams>();
  const [everRendered, setEverRendered] = useState(false);
  const [org, orgRequest] = usePrimaryOrganization();

  const [requests, requestsRequest] = useRequestsForConsumer(org.id, consumerDetails);

  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest, requestsRequest)) {
      setEverRendered(true);
    }
  }, [orgRequest, requestsRequest]);

  if (!everRendered) {
    return <LoadingPageWrapper />;
  }

  const consumerFullName = `${consumerDetails.subjectNameFirst ?? "(any first name)"} ${
    consumerDetails.subjectNameLast ?? "(any last name)"
  }`;

  let pageTitle = null;
  if (consumerDetails?.subjectEmailAddress) {
    pageTitle = consumerDetails?.subjectEmailAddress;
  } else if (consumerDetails?.requestPublicId) {
    pageTitle = `Request ${consumerDetails?.requestPublicId}`;
  } else {
    pageTitle = consumerFullName;
  }

  if (!pageTitle) {
    return (
      <StandardPageWrapper>
        <PageHeader
          className="mb-lg"
          titleContent="No Consumer"
          titleHeaderContent={
            <PageHeaderBack to={Routes.records.recordLookup}>Back to Lookup</PageHeaderBack>
          }
        />
      </StandardPageWrapper>
    );
  }

  const hasRequests = requests.length > 0;

  return (
    <StandardPageWrapper>
      <PageHeader
        className="mb-lg"
        titleContent={pageTitle}
        titleHeaderContent={
          <PageHeaderBack to={Routes.records.recordLookup}>Back to Lookup</PageHeaderBack>
        }
        descriptionContent={
          <Button
            variant="contained"
            href={formatUrl(ApiRouteHelpers.consumer.export(org.id), consumerDetails)}
            target="_blank"
          >
            Export to PDF
          </Button>
        }
      />

      {hasRequests ? (
        <>
          {requests.map((r) => (
            <React.Fragment key={r.id}>
              <DataRequestEventCard startOpen request={r} />
              <Divider className="mt-md mb-xl" />
            </React.Fragment>
          ))}
        </>
      ) : (
        <Empty>No requests for {pageTitle}</Empty>
      )}
    </StandardPageWrapper>
  );
};
